/**
 * HudChip — the ONE pill-shaped control shape for the whole board HUD (view
 * picker, colour-by, web, plane, build-view, layout/dev controls…). Every HUD
 * control derives its shape from here so they all share pill, padding, blur,
 * border and shadow; only the accent state varies.
 *
 * Two entry points off one style source (`hudChipClass`):
 *   - `<HudChip>` — the common case: a `<button>` (forwards its ref, so Radix
 *     `asChild` triggers and popover anchors attach correctly).
 *   - `hudChipClass(...)` — the same class string for the handful of controls
 *     that must wrap a native `<select>` (a select can't live inside a button),
 *     applied to their `<label>` shell.
 */
import { forwardRef, type ButtonHTMLAttributes } from "react";

export type HudChipAccent = "arch" | "emerald" | "amber" | "sky" | "violet";

const ACCENT_ACTIVE: Record<HudChipAccent, string> = {
  arch: "bg-arch-500/(--tint-faint) border-arch-500/(--tint-strong) text-arch-200 hover:bg-arch-500/(--tint-soft)",
  emerald:
    "bg-emerald-500/(--tint-faint) border-emerald-400 text-emerald-200 hover:bg-emerald-500/(--tint-soft)",
  amber: "bg-amber-500/(--tint-faint) border-amber-400 text-amber-200 hover:bg-amber-500/(--tint-soft)",
  sky: "bg-sky-500/(--tint-faint) border-sky-400 text-sky-200 hover:bg-sky-500/(--tint-soft)",
  violet:
    "bg-violet-500/(--tint-faint) border-violet-400 text-violet-200 hover:bg-violet-500/(--tint-soft)",
};

const NEUTRAL = "bg-slate-900/(--tint-strong) border-slate-700 text-slate-300 hover:text-white";

/** The shared HUD pill class. Use directly on non-button shells (e.g. a
 *  `<label>` wrapping a native `<select>`); `<HudChip>` uses it internally. */
export function hudChipClass(opts?: {
  active?: boolean;
  accent?: HudChipAccent;
  className?: string;
}): string {
  const { active = false, accent = "arch", className = "" } = opts ?? {};
  return (
    "inline-flex items-center gap-1.5 rounded-full backdrop-blur-md border " +
    "px-3 py-1.5 text-xs shadow-lg whitespace-nowrap transition-colors " +
    // Bespoke shape, but the focus ring is canonical (DESIGN-SYSTEM §1).
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
    (active ? ACCENT_ACTIVE[accent] : NEUTRAL) +
    (className ? " " + className : "")
  );
}

export interface HudChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** When set, the chip is filled with its accent colour (on/highlighted). */
  readonly active?: boolean;
  /** Accent colour used while `active` (default `arch`). */
  readonly accent?: HudChipAccent;
}

export const HudChip = forwardRef<HTMLButtonElement, HudChipProps>(function HudChip(
  { active = false, accent = "arch", className = "", type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      data-hud
      className={hudChipClass({ active, accent, className })}
      {...rest}
    />
  );
});
